/**
 * The part of a swipe that happens after the finger has gone.
 *
 * The terminal drives its own touch scrolling (touch-action: none, swipe → synthetic
 * wheel, so tmux history moves too), and what it emitted was measurable and thin:
 *
 *   drag 300px  →  150px of scroll, and 0px after the lift
 *
 * Two separate complaints in one number. The content moved at HALF the thumb, so it
 * never sat under the finger; and a flick stopped dead the instant you let go, which
 * no other scrollable surface on a phone does. Owner, 2026-08-22: 手機窄螢幕上使用手指
 * 滑動 Terminal 非常的不順.
 *
 * This is the second half. It is a pure step function rather than a loop with a timer
 * inside so the decay can be tested without pretending to be a clock — and so the
 * numbers below can be argued about on their own.
 */

/** Below this, a flick is over. px/ms — about one pixel per two frames. */
export const FLING_STOP = 0.03
/** Fraction of velocity kept per 16ms. 0.94 ≈ half a second of glide from a fast flick. */
const FLING_DECAY_PER_FRAME = 0.94
/** Beyond this the sampling window is stale — a pause before lifting is not a flick. */
export const FLING_SAMPLE_MS = 90
/** A hard lid, so a freak sample cannot launch the scrollback into orbit. px/ms. */
export const FLING_MAX = 6

/**
 * Velocity to carry out of a drag, from the tail of its samples.
 *
 * The LAST ~90ms only. A finger that swipes fast and then holds still before lifting
 * has ended its gesture with a decision — "stop here" — and averaging over the whole
 * drag would throw the content onward anyway, which feels like the terminal ignoring
 * you. Judging from the tail is what makes press-and-hold a reliable brake.
 */
export function flingVelocity(samples: { t: number; y: number }[]): number {
  if (samples.length < 2) return 0
  const last = samples[samples.length - 1]!
  // Oldest sample still inside the window; falls back to the first we have.
  let first = samples[0]!
  for (const s of samples) {
    if (last.t - s.t <= FLING_SAMPLE_MS) {
      first = s
      break
    }
  }
  const dt = last.t - first.t
  if (dt <= 0) return 0
  const v = (last.y - first.y) / dt
  return Math.max(-FLING_MAX, Math.min(FLING_MAX, v))
}

/**
 * One frame of glide: how far to scroll, and what is left of the velocity.
 *
 * Decay is applied per elapsed millisecond rather than per call, so a frame the
 * browser skipped does not make the flick outlive its budget — the glide has the same
 * shape at 60Hz and at 120Hz, and on a phone that just dropped four frames.
 */
export function flingStep(v: number, dtMs: number): { delta: number; next: number } {
  if (!Number.isFinite(v) || Math.abs(v) < FLING_STOP || dtMs <= 0) return { delta: 0, next: 0 }
  const delta = v * dtMs
  const next = v * FLING_DECAY_PER_FRAME ** (dtMs / 16)
  return { delta, next: Math.abs(next) < FLING_STOP ? 0 : next }
}
